import { getRefListNameByID } from "@/util/common_function";

// Mirrors the legacy BookingQueueStep reflist's per-step "Validator"
// attribute (ValidateBookingAnoka.vbs / config2.txt). Legacy stored a routine
// name on each step and Eval'd it when the step was completed; the React
// ref-list data carries no such attribute, so the step's stable constantName
// is mapped to the routine here, like BOOKING_QUEUE_STEP_PREREQUISITES.
//
// Each validator takes the booking object (Redux `bookings`) and returns ""
// when the step passes, or the user-facing error message (legacy's
// "**VALIDATE**" prefix stripped) when it does not.

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

// Legacy GetArrayNonDeleted: only count rows that haven't been deleted.
const nonDeleted = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((row) => !row?.isDeleted);

// Legacy IsTransitionalHousing: keyed on the Facility Status (Workhouse
// status) default string.
const isTransitionalHousing = (booking) => {
  const statusId = booking?.arrestReports?.[0]?.workhouseStatusId;
  if (isBlank(statusId) || isNaN(statusId)) return false;
  return getRefListNameByID("Workhouse_Status", statusId) ===
    "Transitional Housing";
};

const parseDateTime = (date, time) => {
  if (isBlank(date)) return null;
  const parsed = new Date(isBlank(time) ? date : `${date} ${time}`);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const requireFields = (source, fields) => {
  for (const [key, message] of fields) {
    if (isBlank(source?.[key])) return message;
  }
  return "";
};

// ValidateBQDemographics (Booking Complete step) – required fields only. The
// address / arrest-report / hold-reason / controlling-charge sub-validators
// in the legacy routine are not ported.
const DEMOGRAPHICS_REQUIRED = [
  ["lName", "You must enter the Last Name."],
  ["fName", "You must enter the First Name."],
  ["dob", "You must enter the Date of Birth."],
  ["race", "You must enter the Race."],
  ["sex", "You must enter the Sex."],
];

const DEMOGRAPHICS_REQUIRED_NON_TRANSITIONAL = [
  ["height", "You must enter the Height."],
  ["weight", "You must enter the Weight."],
  ["hair", "You must enter the Hair Color."],
  ["eyes", "You must enter the Eye Color."],
  ["glasses", "You must enter the Glasses."],
  ["hairLength", "You must enter the Hair Length."],
  ["facialHair", "You must enter the Facial Hair."],
  ["complexion", "You must enter the Complexion."],
  ["ethnicity", "You must enter the Ethnicity."],
  ["build", "You must enter the Build."],
  ["hairStyle", "You must enter the Hair Style."],
  ["ssn", "You must enter the SSN."],
  ["pobType", "You must enter the POB Type."],
  ["pobNciCid", "You must enter the POB NCIC."],
];

const validateBQDemographics = (booking) => {
  const bio = booking?.bio?.[0];
  const error = requireFields(bio, DEMOGRAPHICS_REQUIRED);
  if (error) return error;
  if (isTransitionalHousing(booking)) return "";
  return requireFields(bio, DEMOGRAPHICS_REQUIRED_NON_TRANSITIONAL);
};

// ValidateBQIntake -> ValidateIntakeBookingDemographics
const validateBQIntake = (booking) => {
  const bio = booking?.bio?.[0];
  const error = requireFields(bio, DEMOGRAPHICS_REQUIRED);
  if (error) return error;
  // Legacy crn 3124: Age at entry cannot be zero.
  if (!isBlank(bio?.ageAtEntry) && Number(bio.ageAtEntry) === 0) {
    return "Age cannot be zero";
  }
  return "";
};

const validateBQCharges = (booking) => {
  if (isTransitionalHousing(booking)) return "";
  return nonDeleted(booking?.charges).length
    ? ""
    : "You must enter at least one charge to complete the Charges step.";
};

const validateBQAssociations = (booking) =>
  nonDeleted(booking?.bookingsJacketsJackets).length
    ? ""
    : "You must enter an Inmate Associate to complete the Associations step.";

const validateBQClassification = (booking) =>
  nonDeleted(booking?.inmateClassificationQuestionnaires).length
    ? ""
    : "You must fill out a Classification Questionnaire to complete the Classification step.";

const validateBQHousing = (booking) => {
  const arrest = booking?.arrestReports?.[0];
  if (!arrest) return "";
  // Legacy Sugar 1536: ignore on a Permanent Housing change.
  if ((arrest.currentLocation || "").includes("Perm Housing")) return "";
  return isBlank(arrest.housingAssignmentBlock)
    ? "You must assign a permanent housing location to complete the Housing step."
    : "";
};

const validateBQMedical = (booking) => {
  const questionnaires = nonDeleted(booking?.medicalQuestionnaires);
  if (!questionnaires.length) return "You must enter a Questionnaire.";

  // Legacy crn 3525: pass if at least one questionnaire was added after the
  // incarceration date/time (or has no added date/time at all).
  const arrest = booking?.arrestReports?.[0];
  const incarcerated = parseDateTime(
    arrest?.incarcerationDate,
    arrest?.incarcerationTime,
  );
  const foundAfter = questionnaires.some((q) => {
    if (isBlank(q?.dateAdded) && isBlank(q?.timeAdded)) return true;
    const added = parseDateTime(q?.dateAdded, q?.timeAdded);
    if (!added || !incarcerated) return true;
    return added.getTime() > incarcerated.getTime();
  });
  return foundAfter
    ? ""
    : "The medical questionnaire cannot be before the Incarceration Date";
};

const STEP_VALIDATORS = {
  BOOKINGQUEUESTEP_INTAKE: validateBQIntake,
  BOOKINGQUEUESTEP_MEDICAL: validateBQMedical,
  BOOKINGQUEUESTEP_CLASSIFICATION: validateBQClassification,
  BOOKINGQUEUESTEP_CHARGES: validateBQCharges,
  BOOKINGQUEUESTEP_ASSOCIATIONS: validateBQAssociations,
  BOOKINGQUEUESTEP_BOOKINGCOMPLETE: validateBQDemographics,
  BOOKINGQUEUESTEP_HOUSING: validateBQHousing,
};

// Returns "" when the step has no validator or passes; otherwise the error
// message to show the user.
export const validateBookingQueueStep = (constantName, booking) => {
  const validator = STEP_VALIDATORS[constantName];
  return validator ? validator(booking) : "";
};
